import { Pause, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PipelineGateDepths } from '../../utils/orderDetailEnrichment';
import { PIPELINE_SAFE_DEPTH } from '../../utils/orderDetailEnrichment';

type OrderDetailEnrichButtonProps = {
  blankDetailsCount: number;
  missingDetailsCount: number;
  bgEnrichPending: number;
  bgEnrichPaused: boolean;
  onStartOrContinue: (options?: { retryIncomplete?: boolean }) => void;
  onPause?: () => void;
  className?: string;
};

/** Shared Fill missing details / Retry incomplete control (Orders + Profit Compute). */
export function OrderDetailEnrichButton({
  blankDetailsCount,
  missingDetailsCount,
  bgEnrichPending,
  bgEnrichPaused,
  onStartOrContinue,
  onPause,
  className = '',
}: OrderDetailEnrichButtonProps) {
  if (blankDetailsCount <= 0 && bgEnrichPending <= 0 && !bgEnrichPaused) {
    return null;
  }

  const running = bgEnrichPending > 0 && !bgEnrichPaused;

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <Button
        type="button"
        onClick={() => onStartOrContinue({
          retryIncomplete: missingDetailsCount === 0 && blankDetailsCount > 0,
        })}
        loading={running}
        title={
          missingDetailsCount > 0
            ? 'Fill missing customer, origin, and destination from Palletways (up to 10 per click)'
            : 'Retry orders that are still missing details after a previous attempt'
        }
        className="border border-ring/40"
      >
        {running
          ? `Filling details… (${bgEnrichPending} left)`
          : missingDetailsCount > 0
            ? `Fill missing details (${missingDetailsCount})`
            : `Retry incomplete (${blankDetailsCount})`}
      </Button>
      {running && onPause ? (
        <Button
          type="button"
          variant="secondary"
          size="iconSm"
          onClick={onPause}
          title="Pause enrichment"
          aria-label="Pause enrichment"
          className="border border-border/50"
        >
          <Pause className="w-3.5 h-3.5" fill="currentColor" />
        </Button>
      ) : null}
    </div>
  );
}

type OrderDetailEnrichStatusBannersProps = {
  bgEnrichPaused: boolean;
  bgEnrichPending: number;
  pipelineGateDepths: PipelineGateDepths | null;
  enrichUsed: number;
  enrichBudget: number;
  onContinueNow: () => void;
  pipelineSafeDepth?: number;
  /** True when the operator paused via the pause control (not budget/pipeline). */
  userPaused?: boolean;
};

/** Pause / in-progress banners shown above the Orders table (and optionally on Compute). */
export function OrderDetailEnrichStatusBanners({
  bgEnrichPaused,
  bgEnrichPending,
  pipelineGateDepths,
  enrichUsed,
  enrichBudget,
  onContinueNow,
  pipelineSafeDepth = PIPELINE_SAFE_DEPTH,
  userPaused = false,
}: OrderDetailEnrichStatusBannersProps) {
  return (
    <>
      {bgEnrichPaused && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-ring/40 bg-ring/20 px-3 py-2 text-sm text-ring">
          <span>
            {(() => {
              if (userPaused) {
                return bgEnrichPending > 0
                  ? `Enrichment paused (${bgEnrichPending} remaining). Click Continue now or Fill missing details to resume.`
                  : 'Enrichment paused. Click Continue now or Fill missing details to resume.';
              }
              const raw = pipelineGateDepths?.rawImport;
              const commit = pipelineGateDepths?.eventCommit;
              const rawBusy = raw != null && raw > pipelineSafeDepth;
              const commitBusy = commit != null && commit > pipelineSafeDepth;
              if (rawBusy || commitBusy) {
                const parts: string[] = [];
                if (rawBusy) parts.push(`Raw Import ${raw}`);
                if (commitBusy) parts.push(`Event Commit ${commit}`);
                return `Enrichment paused — waiting for pipeline to ease (${parts.join(', ')}; resumes at ≤${pipelineSafeDepth} each)…`;
              }
              return `Background enrichment paused (session limit: ${enrichUsed} / ${enrichBudget}). Resumes when Raw Import and Event Commit are quiet.`;
            })()}
          </span>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onContinueNow}
            className="shrink-0"
          >
            Continue now
          </Button>
        </div>
      )}
      {!bgEnrichPaused && bgEnrichPending > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-ring/40 bg-ring/20 px-3 py-2 text-sm text-ring">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Enriching {bgEnrichPending} orders in background...</span>
        </div>
      )}
    </>
  );
}
